// @ts-nocheck
/*
 * Port of views/dashboard/call-centre/list.js (origin/main-dev, 970 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, API call, toast,
 * role gate, licence-history count fetch, PBX assign flow, switch-to-
 * default-user handler, and JSX condition matches v1 to the letter.
 *
 * Chrome swaps (matching Company List pattern):
 *   - PrimeReact <DataTable> + <Column> → obd3-gui DataTable + ColumnDef
 *   - PrimeReact <Button> → shadcn <Button>
 *   - react-bootstrap Modal kept for delete/pbx/licence-history/licence
 *     modals (v1 already uses these).
 *
 * Cross-module: LicenceHistoryList modal body is a placeholder until
 * v1's licence_history/list.js ports as its own module (same pattern as
 * Company List).
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  API_CALL_CENTRE_LIST,
  API_CALL_CENTRE_SWITCH_DEFAULT_USER,
  API_CALL_CENTRE_DELETE,
  API_CALL_CENTRE_ASSIGN_PBX,
  API_MANAGE_DIALER_LISTING,
  API_CALL_CENTRE_LICENCE,
} from '@/service/url';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { switchUser, getUser } from '@/utilities/setting';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { USER_ROLES, MODULE_ACL, ROUTES } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LicenceDetailsModal from '@/components/partials/components/LicenceDetailsModal';
import LicenceHistoryList from '@/features/licence-history/LicenceHistoryList';
import CallCentreSpendAnalytics from '@/features/call-centres/CallCentreSpendAnalyticsPage';
import {
  BarChart3,
  Download,
  Flag,
  Loader2,
  Plus,
  Pencil,
  Phone,
  Cpu,
  Trash2,
  Building2,
  History,
  Award,
  MoreHorizontal,
  Globe,
  ArrowRightLeft,
  RefreshCw,
} from 'lucide-react';
import CountryCodeDirectionsModal from '@/components/partials/dashboard/country-code-direction/CountryCodeDirectionsModal';
import Loader from '@/components/partials/components/Loader';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

/* Placeholder rendered inside the Licence History modal until the v1
 * licence_history/list.js page ports as its own module. Modal chrome
 * + state is preserved verbatim; only the body content is a stub. */
const CrossModulePlaceholder = ({ label }) => (
  <div className="text-center py-5 text-muted">
    <div style={{ fontSize: 14, marginBottom: 8 }}>
      <strong>{label}</strong>
    </div>
    <div style={{ fontSize: 13 }}>
      This report opens as its own page in v1 — port pending for that module.
    </div>
  </div>
);

const CallCentreList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [callCentreList, setCallCentreList] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    if (tab === 'spend' || tab === 'nested') return tab;
    return 'mine';
  });
  const [loggedInUserACL, setLoggedInUserACL] = useState({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInCompanyDemo, setLoggedInCompanyDemo] = useState(false);
  const [isOwnCompany, setIsOwnCompany] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showLicenceHistoryModal, setShowLicenceHistoryModal] = useState(false);
  const [selectedLicenceCallcentreId, setSelectedLicenceCallcentreId] = useState(null);
  const [selectedLicenceCallcentreName, setSelectedLicenceCallcentreName] = useState(null);
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const [licenceModalId, setLicenceModalId] = useState(null);
  const [licenceModalTitle, setLicenceModalTitle] = useState('');
  const [licenceModalData, setLicenceModalData] = useState(null);
  const [licenceModalLoading, setLicenceModalLoading] = useState(false);
  const [showCcDirectionsModal, setShowCcDirectionsModal] = useState(false);
  const [ccDirectionsData, setCcDirectionsData] = useState([]);
  const [ccDirectionsOwner, setCcDirectionsOwner] = useState('');
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [dialerOptions, setDialerOptions] = useState([]);
  const [showAssignPbxModal, setShowAssignPbxModal] = useState(false);
  const [pbxTargetCallCentre, setPbxTargetCallCentre] = useState(null);
  const [selectedPrimaryPbx, setSelectedPrimaryPbx] = useState('');
  const [selectedSecondaryPbx, setSelectedSecondaryPbx] = useState('');
  const [savingPbx, setSavingPbx] = useState(false);

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUserId(user?.company?.id);
      setCurrentUserDbId(user?.id);
      setCurrentUserRole(user.role);
      const company =
        user?.assignedCompany || user?.assigned_company_details || user?.company || null;
      setLoggedInCompanyDemo(company?.is_demo_company === true);
      setIsOwnCompany(company?.is_own_company === true);
    }
    if (user.role === USER_ROLES.SUPERADMIN) {
      setIsSuperadmin(true);
    }
    if (user?.acl_settings) {
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'call-centers-list',
        data: displayedCallCentreList,
        columns: [
          { header: '#', accessor: 'key' },
          { header: 'Name', accessor: (row) => row.name || '-' },
          { header: 'Domain', accessor: (row) => row.domain || '-' },
          {
            header: 'Created Date',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
          {
            header: 'Updated Date',
            accessor: (row) =>
              new Date(row.updated_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const [refreshing, setRefreshing] = useState(false);
  const fetchCallCentres = (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    getApiCall(`${API_CALL_CENTRE_LIST}?include_nested=true`, {})
      .then(onCallCentreListSuccess)
      .catch(onError)
      .finally(() => (isRefresh ? setRefreshing(false) : setLoading(false)));
  };

  useEffect(() => {
    fetchCallCentres();
  }, []);

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function onCallCentreListSuccess(resp) {
    const updatedList = (Array.isArray(resp) ? resp : []).map((val, idx) => {
      const row = val?.get ? val.get({ plain: true }) : { ...val };
      return { ...row, key: idx + 1 };
    });
    setCallCentreList(updatedList);
  }

  async function switchToDefaultUser(callCentreRow) {
    if (isSwitchingAccount) return;
    const defaultUser = callCentreRow?.defaultUser;
    if (!defaultUser?.id) {
      toast.error('No default user configured for this call center');
      return;
    }
    if (Number(defaultUser.status) !== 1) {
      toast.error('Default user is inactive');
      return;
    }
    setIsSwitchingAccount(true);
    try {
      const url = API_CALL_CENTRE_SWITCH_DEFAULT_USER.replace(':id', callCentreRow.id);
      const response = await postApiCall(url, {});
      const switchAccountSuccess = await switchUser(response);
      if (switchAccountSuccess) {
        toast.success('Switched to call center default admin');
        setTimeout(() => {
          window.location.href = ROUTES.DASHBOARD;
        }, 1500);
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to switch user');
      setIsSwitchingAccount(false);
    }
  }

  const handleAllocateClick = (id, role) => {
    navigate(`/allocate/${id}/${currentUserId}/callcentre`);
  };

  useEffect(() => {
    if (!isSuperAdmin || !currentUserDbId) return;
    getApiCall(API_MANAGE_DIALER_LISTING, {})
      .then((resp) => {
        const all = Array.isArray(resp) ? resp : [];
        const ownDialers = all.filter(
          (d) => Number(d?.created_by) === Number(currentUserDbId),
        );
        setDialerOptions(
          ownDialers.length > 0 || all.every((d) => d?.created_by === undefined)
            ? ownDialers.length > 0
              ? ownDialers
              : all
            : ownDialers,
        );
      })
      .catch((err) => {
        console.log('Failed to load PBX list', err);
        setDialerOptions([]);
      });
  }, [isSuperAdmin, currentUserDbId]);

  const openAssignPbxModal = (rowData) => {
    setPbxTargetCallCentre(rowData);
    setSelectedPrimaryPbx(rowData?.primary_pbx != null ? String(rowData.primary_pbx) : '');
    setSelectedSecondaryPbx(rowData?.secondary_pbx != null ? String(rowData.secondary_pbx) : '');
    setShowAssignPbxModal(true);
  };

  const closeAssignPbxModal = () => {
    if (savingPbx) return;
    setShowAssignPbxModal(false);
    setPbxTargetCallCentre(null);
    setSelectedPrimaryPbx('');
    setSelectedSecondaryPbx('');
  };

  const handleSaveAssignPbx = async () => {
    if (!pbxTargetCallCentre?.id) return;
    if (!selectedPrimaryPbx) {
      toast.error('Primary PBX is required');
      return;
    }
    if (selectedSecondaryPbx && selectedSecondaryPbx === selectedPrimaryPbx) {
      toast.error('Primary and secondary PBX cannot be the same');
      return;
    }
    try {
      setSavingPbx(true);
      const url = API_CALL_CENTRE_ASSIGN_PBX.replace(':id', pbxTargetCallCentre.id);
      await postApiCall(url, {
        primary_pbx: Number(selectedPrimaryPbx),
        secondary_pbx: selectedSecondaryPbx ? Number(selectedSecondaryPbx) : null,
      });
      toast.success('PBX assigned successfully');
      setShowAssignPbxModal(false);
      setPbxTargetCallCentre(null);
      setSelectedPrimaryPbx('');
      setSelectedSecondaryPbx('');
      const resp = await getApiCall(`${API_CALL_CENTRE_LIST}?include_nested=true`, {});
      onCallCentreListSuccess(resp);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to assign PBX');
    } finally {
      setSavingPbx(false);
    }
  };

  const pbxNameById = (id) => {
    if (id == null) return null;
    const match = dialerOptions.find((d) => Number(d.dialer_id) === Number(id));
    return match?.dialer_name || null;
  };

  const fetchCallCentreLicence = async (callCentreId) => {
    if (!callCentreId) return;
    try {
      setLicenceModalLoading(true);
      const url = API_CALL_CENTRE_LICENCE.replace(':id', callCentreId);
      const resp = await getApiCall(url, {});
      setLicenceModalData(resp || null);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to load licence');
      setLicenceModalData(null);
    } finally {
      setLicenceModalLoading(false);
    }
  };

  const openCallCentreLicence = (rowData) => {
    setLicenceModalId(rowData.id);
    setLicenceModalTitle(`Licence — ${rowData.name || ''}`);
    setLicenceModalData(null);
    setShowLicenceModal(true);
    fetchCallCentreLicence(rowData.id);
  };

  function openDeleteModal(id) {
    setDeleteId(id);
    setShowDeleteModal(true);
  }

  function closeDeleteModal() {
    setDeleteId(null);
    setShowDeleteModal(false);
  }

  async function confirmDelete() {
    try {
      setButtonDisable(true);
      const url = API_CALL_CENTRE_DELETE.replace(':id', deleteId);
      await postApiCall(url, {});
      toast.success('Call centre deleted successfully');
      closeDeleteModal();
      setLoading(true);
      const resp = await getApiCall(`${API_CALL_CENTRE_LIST}?include_nested=true`, {});
      onCallCentreListSuccess(resp);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to delete call center');
      onError(err);
    } finally {
      setLoading(false);
      setButtonDisable(false);
    }
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCreateButtonClick = () => {
    console.log('Check one');
    navigate(`/${ROUTES.CALL_CENTRES.CREATE}`);
  };

  const isNestedTab = activeTab === 'nested';

  const [myCallCentreList, nestedCallCentreList] = useMemo(() => {
    const mine = [];
    const nested = [];
    callCentreList.forEach((row) => (row?.is_nested ? nested : mine).push(row));
    return [mine, nested];
  }, [callCentreList]);

  const tabCallCentreList = isNestedTab ? nestedCallCentreList : myCallCentreList;

  const displayedCallCentreList = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return tabCallCentreList;
    return tabCallCentreList.filter((cc) => (cc?.name || '').toLowerCase().includes(q));
  }, [tabCallCentreList, searchValue]);

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Call Centres List' /* backend ACL key — do not Americanise */,
    'read',
  );
  const isCallCentreProvisioner =
    currentUserRole === USER_ROLES.COMPANY_ADMIN || isOwnCompany;
  const canAdd =
    isCallCentreProvisioner &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CALL_CENTRE, 'add');
  const canEdit =
    isCallCentreProvisioner &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CALL_CENTRE, 'edit');
  const canDelete =
    isCallCentreProvisioner &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CALL_CENTRE, 'delete');

  const selectTab = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'mine') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  function RenderActionComponent(rowData) {
    const id = rowData.id;
    const editUrl = ROUTES.CALL_CENTRES.EDIT.replace(':id', id);
    const canRowEdit = canEdit && !isNestedTab;
    return (
      <div className="flex items-center gap-1">
        {canRowEdit && (
          <button
            type="button"
            title="Edit call center"
            onClick={() => navigate(`/${editUrl}`)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {canEdit && !isNestedTab && (
          <button
            type="button"
            title="Allocate number"
            onClick={() => handleAllocateClick(id, rowData.name)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-sky-100 text-sky-700 transition-colors hover:bg-sky-200 dark:bg-sky-950/40 dark:text-sky-300"
          >
            <Phone className="h-3.5 w-3.5" />
          </button>
        )}
        {isSuperAdmin && !isNestedTab && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title="More"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors hover:bg-muted/80"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openAssignPbxModal(rowData)}>
                <Cpu className="h-4 w-4" /> Assign PBX
              </DropdownMenuItem>
              {canDelete && !loggedInCompanyDemo && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => openDeleteModal(id)}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'key',
        header: '#',
        size: 60,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground tabular-nums">{row.original.key}</span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 240,
        cell: ({ row }) => {
          const nm = row.original.name || '—';
          const initials = (nm || '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w: string) => w[0])
            .join('')
            .toUpperCase() || '—';
          return (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
                {initials}
              </span>
              <span className="font-medium text-foreground" title={nm}>
                {nm}
              </span>
            </div>
          );
        },
      },
      {
        id: 'domain',
        accessorFn: (r) => r.domain || '',
        header: 'Domain',
        cell: ({ row }) => {
          const domain = row.original.domain;
          if (!domain) return <span className="text-muted-foreground">—</span>;
          return (
            <span
              className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold text-foreground ring-1 ring-inset ring-border/60"
              title={domain}
            >
              <Globe className="h-3 w-3 opacity-60" />
              <span className="line-clamp-1 max-w-[220px]">{domain}</span>
            </span>
          );
        },
      },
      {
        id: 'country',
        header: 'Country',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => {
          const rows = (row.original.callcenterCountryCodeDirections || []).filter(
            (r) => r.directionMaster && !r.is_deleted,
          );
          if (rows.length === 0) return <span className="text-muted-foreground">—</span>;
          return (
            <button
              type="button"
              title="View country codes"
              onClick={() => {
                setCcDirectionsData(rows);
                setCcDirectionsOwner(row.original.name);
                setShowCcDirectionsModal(true);
              }}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 transition-colors hover:bg-sky-200 dark:bg-sky-950/40 dark:text-sky-300"
            >
              <Flag className="h-3 w-3" />
              {rows.length} code{rows.length === 1 ? '' : 's'}
            </button>
          );
        },
      },
      {
        id: 'licence',
        header: 'Licence',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => {
          const rowData = row.original;
          if (!rowData.id) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="flex flex-col items-start gap-1">
              <button
                type="button"
                title="Licence history"
                onClick={() => {
                  setSelectedLicenceCallcentreId(rowData.id);
                  setSelectedLicenceCallcentreName(rowData.name);
                  setShowLicenceHistoryModal(true);
                }}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-200 transition-colors hover:bg-violet-200 dark:bg-violet-950/40 dark:text-violet-300"
              >
                <History className="h-3 w-3" />
                History
              </button>
              <button
                type="button"
                title="View licence"
                onClick={() => openCallCentreLicence(rowData)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200 transition-colors hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-300"
              >
                <Award className="h-3 w-3" />
                View licence
              </button>
            </div>
          );
        },
      },
      {
        id: 'pbx',
        header: 'PBX',
        cell: ({ row }) => {
          const rowData = row.original;
          const primary = rowData?.primaryPbx?.dialer_name || pbxNameById(rowData?.primary_pbx) || '—';
          const secondary = rowData?.secondaryPbx?.dialer_name || pbxNameById(rowData?.secondary_pbx) || '—';
          return (
            <div className="flex flex-col gap-0.5 whitespace-nowrap text-xs">
              <div><span className="text-muted-foreground">Primary: </span><span className="text-foreground">{primary}</span></div>
              <div><span className="text-muted-foreground">Secondary: </span><span className="text-foreground">{secondary}</span></div>
            </div>
          );
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {formatShortDate(row.original.updated_at)}
          </span>
        ),
      },
      {
        id: 'defaultUser',
        accessorFn: (r) => r.defaultUser?.username || '',
        header: 'Default User',
        enableSorting: false,
        // enableHiding: false — hosts the switch-to-default-user button
        // (only entry point to switch into a call-centre tenant). Same
        // guard as UserListPage 'action' and CompanyListPage 'defaultUser'.
        enableHiding: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const defaultUser = rowData.defaultUser;
          const canSwitchDefaultUser =
            Boolean(defaultUser?.id) && Number(defaultUser?.status) === 1;
          const username = defaultUser?.username || '—';
          return (
            <div className="flex items-center gap-2">
              {canSwitchDefaultUser ? (
                <button
                  type="button"
                  title={isSwitchingAccount ? 'Switching…' : `Switch to ${username}`}
                  disabled={isSwitchingAccount}
                  onClick={() => switchToDefaultUser(rowData)}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15 disabled:opacity-50"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5" />
                </button>
              ) : (
                <span className="h-7 w-7 shrink-0" aria-hidden />
              )}
              <span className="text-sm text-foreground" title={username}>
                {username}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => RenderActionComponent(row.original),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dialerOptions,
      isSwitchingAccount,
      canEdit,
      canDelete,
      isSuperAdmin,
      loggedInCompanyDemo,
      isNestedTab,
    ],
  );

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Call Centers" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading && callCentreList.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  // Stat strip data — scoped to the active tab
  const totalCenters = tabCallCentreList.length;
  const withPbx = tabCallCentreList.filter(
    (cc: any) => cc?.primary_pbx || cc?.primaryPbx?.dialer_name,
  ).length;

  return (
    <FillPage>
      <PageHeader
        title="Call Centers"
        description="Tenants provisioned on the platform. Each has its own default admin, PBX pair and licence."
        actions={
          <>
            {activeTab !== 'spend' && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => fetchCallCentres(true)}
                disabled={refreshing || loading}
                title="Refresh"
                aria-label="Refresh"
              >
                <RefreshCw className={'h-4 w-4 ' + (refreshing ? 'animate-spin' : '')} />
              </Button>
            )}
            {activeTab !== 'spend' && tabCallCentreList.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={handleCreateButtonClick}>
                <Plus className="h-4 w-4" />
                Create Call Center
              </Button>
            )}
          </>
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={selectTab}
        className="mb-4"
      >
        <TabsList className="h-auto max-w-full shrink-0 justify-start gap-1 overflow-x-auto rounded-full border border-border/60 bg-muted/40 p-1 [scrollbar-width:none] shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]">
          <TabsTrigger
            value="mine"
            className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
          >
            <Building2 className="h-3.5 w-3.5" />
            My Call Centers ({myCallCentreList.length})
          </TabsTrigger>
          <TabsTrigger
            value="nested"
            className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
          >
            <Building2 className="h-3.5 w-3.5" />
            Created By Child Users ({nestedCallCentreList.length})
          </TabsTrigger>
          <TabsTrigger
            value="spend"
            className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
          >
            <BarChart3 className="h-3.5 w-3.5" />
            Spend Analytics
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'spend' ? (
        <CallCentreSpendAnalytics embedded />
      ) : (
      <>
      {/* Stat strip — Total centers + PBX-assigned count. */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Building2 className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">Total call centers</span>
          <span className="font-semibold text-foreground tabular-nums">{totalCenters}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-sky-900/60 dark:bg-sky-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
            <Cpu className="h-3.5 w-3.5" />
          </span>
          <span className="text-sky-700 dark:text-sky-300">PBX assigned</span>
          <span className="font-semibold text-sky-800 tabular-nums dark:text-sky-200">
            {withPbx}
          </span>
        </div>
      </div>

      <DataTable
        key={activeTab}
        tableId={isNestedTab ? 'callcentres.nested:v2' : 'callcentres.list:v3'}
        columns={columns}
        data={displayedCallCentreList}
        isLoading={loading}
        searchPlaceholder="Search by name, domain, default user…"
        emptyTitle={isNestedTab ? 'No nested call centers' : 'No call centers'}
        emptyDescription={
          isNestedTab
            ? 'None of your nested users have created a call center yet.'
            : 'No call centers match the current filters.'
        }
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={8}
        disableExport
      />
      </>
      )}

      <ConfirmDialog
        open={showDeleteModal}
        onOpenChange={(o) => !o && closeDeleteModal()}
        title="Delete Call Center"
        description="Are you sure you want to delete this call center? Licence values cannot be returned."
        confirmLabel="Delete"
        tone="destructive"
        loading={buttonDisable}
        onConfirm={confirmDelete}
      />

      {/* Licence History — placeholder body until v1 licence_history/list.js ports */}
      <Dialog
        open={showLicenceHistoryModal}
        onOpenChange={(next) => {
          if (!next) {
            setShowLicenceHistoryModal(false);
            setSelectedLicenceCallcentreId(null);
            setSelectedLicenceCallcentreName(null);
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <History className="h-4 w-4" />
              </span>
              <span className="flex flex-col">
                <span>Licence History</span>
                {selectedLicenceCallcentreName && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {selectedLicenceCallcentreName}
                  </span>
                )}
              </span>
            </DialogTitle>
          </DialogHeader>
          {selectedLicenceCallcentreId && (
            <LicenceHistoryList
              callcenterId={selectedLicenceCallcentreId}
              displayName={selectedLicenceCallcentreName}
            />
          )}
        </DialogContent>
      </Dialog>

      <LicenceDetailsModal
        show={showLicenceModal}
        onHide={() => setShowLicenceModal(false)}
        title={licenceModalTitle}
        licence={licenceModalData}
        loading={licenceModalLoading}
        onRefresh={() => fetchCallCentreLicence(licenceModalId)}
      />

      <Dialog open={showAssignPbxModal} onOpenChange={(next) => !next && closeAssignPbxModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Assign PBX{pbxTargetCallCentre?.name ? ` — ${pbxTargetCallCentre.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primary_pbx">
                Primary PBX <span className="text-destructive">*</span>
              </Label>
              <Select value={selectedPrimaryPbx} onValueChange={setSelectedPrimaryPbx}>
                <SelectTrigger id="primary_pbx">
                  <SelectValue placeholder="Select primary PBX" />
                </SelectTrigger>
                <SelectContent>
                  {dialerOptions.map((d) => (
                    <SelectItem key={d.dialer_id} value={String(d.dialer_id)}>
                      {d.dialer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Primary PBX is required and cannot be removed, but can be changed.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondary_pbx">Secondary PBX</Label>
              <Select
                value={selectedSecondaryPbx || '__none__'}
                onValueChange={(v) => setSelectedSecondaryPbx(v === '__none__' ? '' : v)}
              >
                <SelectTrigger id="secondary_pbx">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {dialerOptions
                    .filter((d) => String(d.dialer_id) !== selectedPrimaryPbx)
                    .map((d) => (
                      <SelectItem key={d.dialer_id} value={String(d.dialer_id)}>
                        {d.dialer_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Optional — can be left as None.</p>
            </div>
            {dialerOptions.length === 0 && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                No PBX created by you is available to assign.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAssignPbxModal} disabled={savingPbx}>
              Cancel
            </Button>
            <Button onClick={handleSaveAssignPbx} disabled={savingPbx || !selectedPrimaryPbx}>
              {savingPbx && <Loader2 className="h-4 w-4 animate-spin" />}
              {savingPbx ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CountryCodeDirectionsModal
        show={showCcDirectionsModal}
        onHide={() => {
          setShowCcDirectionsModal(false);
          setCcDirectionsData([]);
          setCcDirectionsOwner('');
        }}
        title="Call Center Country Codes"
        ownerLabel="Call Center Name"
        ownerName={ccDirectionsOwner}
        directions={ccDirectionsData}
      />
    </FillPage>
  );
};

export default CallCentreList;
